import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ApiError, api } from "@/lib/api";
import { money, transitCopy } from "@/lib/format";
import type { Address, Parcel, Quote, RateOption } from "@/lib/types";
import { Alert, Badge, Button, Card, Field, Spinner, cx } from "@/components/ui";
import { AddressForm, EMPTY_ADDRESS } from "@/components/AddressForm";
import { LogoMark } from "@/components/Logo";
import { ParcelFields, describedParcel, isProhibited } from "@/components/ParcelFields";
import {
  EMPTY_SCHEDULE,
  SchedulePicker,
  formatDate,
  formatDeliveryEstimate,
  type ScheduleSelection,
} from "@/components/SchedulePicker";
import { EMPTY_PARCEL } from "@/lib/parcelCatalog";

const STEPS = ["Addresses", "Parcels", "Pay"] as const;

/**
 * Booking without an account.
 *
 * Behind the scenes this still creates an account -- everything from quoting
 * to the wallet to the ShipLogic billing mirror expects one to exist -- but
 * the guest never sees a password field or a login. They see an address
 * form, a price, and a card page. The shipment is booked automatically the
 * moment that one payment clears.
 */
export function GuestBook() {
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [collection, setCollection] = useState<Address>({ ...EMPTY_ADDRESS });
  const [delivery, setDelivery] = useState<Address>({ ...EMPTY_ADDRESS });
  const [parcels, setParcels] = useState<Parcel[]>([{ ...EMPTY_PARCEL }]);
  const [categoryIds, setCategoryIds] = useState<(string | null)[]>([null]);
  const [declaredValue, setDeclaredValue] = useState("");
  const [insured, setInsured] = useState(false);
  const [schedule, setSchedule] = useState<ScheduleSelection>(EMPTY_SCHEDULE);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [selected, setSelected] = useState<RateOption | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState("");

  const quoteMutation = useMutation({
    mutationFn: () =>
      api.publicPost<Quote>("/v1/guest/quotes", {
        email,
        collection,
        delivery,
        parcels: parcels.map((parcel, index) => describedParcel(parcel, categoryIds[index] ?? null)),
        declared_value_cents: declaredValue ? Math.round(Number(declaredValue) * 100) : 0,
        insurance: insured,
      }),
    onSuccess: (result) => {
      setQuote(result);
      setSelected(result.options.find((option) => option.badge === "recommended") ?? result.options[0] ?? null);
      setFieldErrors({});
      setMessage("");
      setStep(2);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fields);
        setMessage(Object.keys(error.fields).length ? "" : error.message);
        if (Object.keys(error.fields).some((key) => key === "email" || key.startsWith("collection") || key.startsWith("delivery"))) {
          setStep(0);
        } else if (Object.keys(error.fields).some((key) => key.startsWith("parcel"))) {
          setStep(1);
        }
      } else {
        setMessage("Could not fetch rates. Try again.");
      }
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: () =>
      api.publicPost<{ payment_id: string; redirect_url: string }>("/v1/guest/shipments", {
        quote_id: quote?.id,
        option_id: selected?.id,
        collection_date: schedule.collectionDate || undefined,
        requested_collection_after: schedule.collectionAfter || undefined,
        requested_collection_before: schedule.collectionBefore || undefined,
        requested_delivery_after: schedule.deliveryAfter || undefined,
        requested_delivery_before: schedule.deliveryBefore || undefined,
      }),
    onSuccess: (result) => {
      window.location.href = result.redirect_url;
    },
    onError: (error) => {
      if (error instanceof ApiError && Object.keys(error.fields).length) {
        setFieldErrors(error.fields);
        setMessage(error.message);
      } else {
        setMessage(error instanceof ApiError ? error.message : "Could not start payment. Try again.");
      }
    },
  });

  const totalWeight = useMemo(() => parcels.reduce((sum, parcel) => sum + Number(parcel.weight_g || 0), 0), [parcels]);

  const contactComplete = email.includes("@");
  const addressesComplete =
    contactComplete && [collection, delivery].every((a) => a.contact_name && a.phone && a.street && a.suburb && a.city && a.postal_code);

  function updateParcel(index: number, patch: Partial<Parcel>) {
    setParcels((prev) => prev.map((parcel, i) => (i === index ? { ...parcel, ...patch } : parcel)));
  }

  function addParcel() {
    setParcels((prev) => [...prev, { ...EMPTY_PARCEL }]);
    setCategoryIds((prev) => [...prev, null]);
  }

  function removeParcel(index: number) {
    setParcels((prev) => prev.filter((_, i) => i !== index));
    setCategoryIds((prev) => prev.filter((_, i) => i !== index));
  }

  const hasProhibited = categoryIds.some((id) => isProhibited(id));

  return (
    <div className="min-h-dvh bg-ink-100">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2 text-lg font-semibold tracking-tight">
            <LogoMark className="h-7 w-7" />
            WeShip
          </Link>
          <Link to="/login" className="text-sm font-medium text-brand">
            Have an account? Sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8">
        <h1 className="text-2xl font-semibold tracking-tight">Book a shipment, no account needed</h1>
        <p className="mt-1 text-sm text-ink-500">Pay for this one collection by card. Your receipt and tracking link go to your email.</p>

        <ol className="my-6 flex flex-wrap gap-2">
          {STEPS.map((label, index) => (
            <li key={label}>
              <button
                type="button"
                disabled={index > step}
                onClick={() => setStep(index)}
                className={cx(
                  "rounded-lg px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40",
                  index === step ? "bg-ink-900 text-white" : "bg-white text-ink-600",
                )}
              >
                {index + 1}. {label}
              </button>
            </li>
          ))}
        </ol>

        {message && (
          <div className="mb-4">
            <Alert>{message}</Alert>
          </div>
        )}

        {step === 0 && (
          <div className="space-y-5">
            <Card>
              <h2 className="mb-4 text-lg font-semibold tracking-tight">Your email</h2>
              <Field
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={fieldErrors.email}
                hint="We send your receipt and tracking link here. No account is created that you can log into."
              />
            </Card>

            {/* Side by side from lg up, same as the account-holder booking page. */}
            <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
              <Card>
                <h2 className="mb-4 text-lg font-semibold tracking-tight">Collect from</h2>
                <AddressForm prefix="collection" value={collection} onChange={setCollection} errors={fieldErrors} />
              </Card>
              <Card>
                <h2 className="mb-4 text-lg font-semibold tracking-tight">Deliver to</h2>
                <AddressForm prefix="delivery" value={delivery} onChange={setDelivery} errors={fieldErrors} />
              </Card>
            </div>

            <div className="flex justify-end">
              <Button disabled={!addressesComplete} onClick={() => setStep(1)}>
                Continue to parcels
              </Button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5">
            {parcels.map((parcel, index) => (
              <Card key={index}>
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold tracking-tight">Parcel {index + 1}</h2>
                  {parcels.length > 1 && (
                    <Button size="sm" variant="ghost" onClick={() => removeParcel(index)}>
                      Remove
                    </Button>
                  )}
                </div>
                <ParcelFields
                  parcel={parcel}
                  categoryId={categoryIds[index] ?? null}
                  onChangeParcel={(patch) => updateParcel(index, patch)}
                  onChangeCategoryId={(next) => setCategoryIds((prev) => prev.map((c, i) => (i === index ? next : c)))}
                  errors={fieldErrors}
                  prefix={`parcels[${index}]`}
                />
              </Card>
            ))}

            <div className="flex flex-wrap items-center gap-3">
              <Button variant="secondary" onClick={addParcel}>
                Add another parcel
              </Button>
              <p className="text-sm text-ink-400">
                {parcels.length} parcel{parcels.length === 1 ? "" : "s"}, {(totalWeight / 1000).toFixed(2)} kg total
              </p>
            </div>

            <Card>
              <Field
                label="Declared value (R)"
                type="number"
                min={0}
                value={declaredValue}
                onChange={(e) => setDeclaredValue(e.target.value)}
                error={fieldErrors.declared_value_cents}
                hint="Required if you want cover for loss or damage."
              />
              <label className="mt-4 flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={insured}
                  onChange={(e) => setInsured(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand"
                />
                <span>
                  Add insurance cover
                  <span className="block text-xs text-ink-400">Charged as a percentage of declared value and shown in the price.</span>
                </span>
              </label>
            </Card>

            {hasProhibited && (
              <p className="text-sm text-ink-400">
                Remove or change the flagged item above before continuing -- we can't ship it by courier.
              </p>
            )}

            <div className="flex justify-between">
              <Button variant="secondary" onClick={() => setStep(0)}>
                Back
              </Button>
              <Button disabled={hasProhibited} loading={quoteMutation.isPending} onClick={() => quoteMutation.mutate()}>
                Get a price
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            {quoteMutation.isPending ? (
              <Card className="flex items-center gap-3 text-sm text-ink-400">
                <Spinner /> Working out your options
              </Card>
            ) : !quote?.options.length ? (
              <Alert tone="warn">We cannot ship this consignment. Check the addresses and parcel weights.</Alert>
            ) : (
              <div className="grid gap-3">
                {quote.options.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setSelected(option)}
                    className={cx(
                      "ws-card flex flex-wrap items-center gap-4 p-5 text-left transition",
                      selected?.id === option.id ? "border-brand ring-2 ring-brand/20" : "hover:border-ink-300",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium">{option.service_name}</p>
                        {option.badge && (
                          <Badge tone={option.badge === "cheapest" ? "good" : option.badge === "fastest" ? "progress" : "neutral"}>
                            {option.badge}
                          </Badge>
                        )}
                      </div>
                      {option.service_note && <p className="mt-0.5 text-sm text-ink-500">{option.service_note}</p>}
                      <p className="mt-1 text-sm text-ink-400">
                        {option.earliest_collection_date
                          ? `Collected ${formatDate(option.earliest_collection_date)}`
                          : transitCopy(option.transit_days)}
                        {formatDeliveryEstimate(option) ? `, delivered ${formatDeliveryEstimate(option)}` : ""}
                      </p>
                      {option.collection_cutoff && (
                        <p className="mt-0.5 text-xs text-ink-400">Collection cut-off {option.collection_cutoff}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold">{money(option.price_cents)}</p>
                      <p className="text-xs text-ink-400">incl. {money(option.vat_cents)} VAT</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {selected && (
              <Card>
                <h2 className="mb-1 text-lg font-semibold tracking-tight">When</h2>
                <p className="mb-4 text-sm text-ink-400">
                  {selected.collection_cutoff
                    ? `${selected.service_name} collects by ${selected.collection_cutoff}.`
                    : `What ${selected.service_name} can collect and deliver.`}
                </p>
                <SchedulePicker option={selected} value={schedule} onChange={setSchedule} errors={fieldErrors} />
              </Card>
            )}

            {selected && (
              <Card>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-sm text-ink-400">Total to pay now</p>
                    <p className="text-3xl font-semibold tracking-tight">{money(selected.price_cents)}</p>
                    <p className="text-xs text-ink-400">Includes {money(selected.vat_cents)} VAT. Charged once, by card.</p>
                  </div>
                  <Button loading={checkoutMutation.isPending} onClick={() => checkoutMutation.mutate()}>
                    Pay and book
                  </Button>
                </div>
              </Card>
            )}

            <Button variant="secondary" onClick={() => setStep(1)}>
              Back
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
